import React, { useState } from 'react';
import PropTypes from 'prop-types';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import useForm from '@afosto/utils-react/validation/useForm';
import useCountries from '@afosto/utils-react/useCountries';
import Checkbox from '../Checkbox';
import Form from '../Form';
import translations from './translations';
import useIntl from '@afosto/utils-react/intl/useIntl';
import camelize from '@afosto/utils/string/camelize';
import { object } from 'yup';
import AddressInput from '../AddressInput';

const AddressForm = ({
  address,
  onCancel,
  onSubmit,
  showSetAsPrimary,
  showSetAsBillingAddress,
  showSetAsShippingAddress,
}) => {
  const intl = useIntl();
  const rawCountries = useCountries();
  const [validationSchema, setValidationSchema] = useState({});

  const {
    given_name: givenName = '',
    additional_name: additionalName = '',
    family_name: familyName = '',
    organisation = '',
    country_code: country = 'NL',
    thoroughfare = '',
    premise_number: premiseNumber = '',
    premise_number_suffix: premiseNumberSuffix = '',
    locality = '',
    postal_code: postalCode = '',
    administrative_area: administrativeArea = '',
    isPrimary = false,
  } = address;

  const countries = rawCountries.map(rawCountry => ({
    label: rawCountry.name,
    ...rawCountry,
  }));
  const formattedCountry = countries.find(({ value }) => value === country);

  const form = useForm({
    validationSchema: object().shape(validationSchema),
    defaultValues: {
      givenName,
      additionalName,
      familyName,
      organisation,
      country: formattedCountry ?? null,
      thoroughfare,
      premiseNumber,
      premiseNumberSuffix,
      locality,
      postalCode,
      administrativeArea,
      isPrimary,
      setAsBillingAddress: false,
      setAsShippingAddress: false,
    },
    onCancel,
    onSubmit: async data => {
      if (onSubmit) {
        const { country: countryData, ...otherData } = data;
        const response = onSubmit({
          ...otherData,
          id: address.id || null,
          countryCode: countryData.value,
        });
        response.catch(err => {
          const { errors = {} } = err?.response?.data || {};
          if (errors?.invalid) {
            errors.invalid.map(error =>
              form.setError(camelize(error), 'invalidValue', 'This value is invalid'),
            );
          }
        });
      }
    },
  });

  const onFetchAddressRules = ({ rules }) => {
    setValidationSchema(rules);
  };

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form}>
        <AddressInput onfetchAddressRules={onFetchAddressRules} />
        {showSetAsPrimary && (
          <Checkbox name="isPrimary" label={intl.formatMessage(translations.fields.isPrimary)} />
        )}

        {showSetAsBillingAddress && (
          <Checkbox
            name="setAsBillingAddress"
            label={intl.formatMessage(translations.fields.setAsBillingAddress)}
          />
        )}

        {showSetAsShippingAddress && (
          <Checkbox
            name="setAsShippingAddress"
            label={intl.formatMessage(translations.fields.setAsShippingAddress)}
          />
        )}
      </Form>
    </LoadingWrapper>
  );
};

AddressForm.propTypes = {
  address: PropTypes.object,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  showSetAsPrimary: PropTypes.bool,
  showSetAsBillingAddress: PropTypes.bool,
  showSetAsShippingAddress: PropTypes.bool,
};

AddressForm.defaultProps = {
  address: {},
  onSubmit: undefined,
  onCancel: undefined,
  showSetAsPrimary: false,
  showSetAsBillingAddress: false,
  showSetAsShippingAddress: false,
};

export default AddressForm;
